import { SimpleMoney } from "@oysterhr/common"
import { useDimensionsContext } from "./DimensionsProvider"
import { useMemo } from "react"
import { roundNumberFromMoney } from "./utils"
import { cn } from "~/app/utils/styleHelpers"
import { BandChartDataPoint } from "./types"

export type BandProps = {
  data: BandChartDataPoint
  currencyCode: string
  index: number
  roundValues?: boolean
  onClick?: () => void
  isFiguresData?: boolean
}

export const BAND_HEIGHT = 32
export const BAND_SPACING = 24
export const BAND_HEIGHT_WITH_NUMBERS = BAND_HEIGHT + 20
export const BAND_TOTAL_HEIGHT = BAND_HEIGHT_WITH_NUMBERS + BAND_SPACING
const BAND_TEXT_SPACING = 4
const BAND_ROUNDNESS = 4
const BAND_TEXT_SIZE = 10

const figuresColors = {
  base: "#FF2A64",
  light: "#FFCDDB",
}

export const Band = ({
  data,
  currencyCode,
  index,
  roundValues,
  onClick,
  isFiguresData,
}: BandProps) => {
  const { lowestTick, scale, boundedHeight } = useDimensionsContext()

  const XPosP10 = useMemo(
    () => (data.p10 ? (data.p10 - lowestTick) / scale : 0),
    [lowestTick, scale, data],
  )
  const XPosP90 = useMemo(
    () => (data.p90 ? (data.p90 - lowestTick) / scale : 0),
    [scale, data, lowestTick],
  )
  const XPos = useMemo(() => (data.min - lowestTick) / scale, [lowestTick, scale, data])

  const basePosition = boundedHeight

  const YPos = useMemo(() => basePosition - BAND_TOTAL_HEIGHT * (index + 1), [index, basePosition])

  const p10width = useMemo(() => {
    return data.p10 ? (data.min - data.p10) / scale : 0
  }, [scale, data])
  const width = useMemo(() => {
    return (data.max - data.min) / scale
  }, [scale, data])
  const p90width = useMemo(() => {
    return data.p90 ? (data.p90 - data.max) / scale : 0
  }, [scale, data])

  const BenchmarkXPos = useMemo(
    () => (data.benchmark - lowestTick) / scale,
    [lowestTick, scale, data],
  )

  const roundIfEnabled = (money: string) => (roundValues ? roundNumberFromMoney(money) : money)

  return (
    <>
      {/* Minimum value */}
      {!isFiguresData && (
        <text
          key={data.min}
          style={{
            fontSize: BAND_TEXT_SIZE,
            textAnchor: "middle",
            transform: `translateY(${BAND_TEXT_SIZE + BAND_TEXT_SPACING}px)`,
            ...(isFiguresData ? { stroke: figuresColors.base } : undefined),
          }}
          className="stroke-info-base"
          x={XPos}
          y={YPos + BAND_HEIGHT}
        >
          {roundIfEnabled(
            new SimpleMoney(data.min, currencyCode).format({
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
          )}
        </text>
      )}

      {/* p10 vertical line */}
      {isFiguresData && data.p10 && (
        <rect
          height={12}
          width={1}
          x={XPosP10}
          y={YPos + 10}
          style={{ fill: figuresColors.base }}
        />
      )}

      {/* p10 - 25 horizontal line */}
      {isFiguresData && data.p10 && (
        <rect
          width={p10width}
          height={1}
          rx={BAND_ROUNDNESS}
          ry={BAND_ROUNDNESS}
          style={{ fill: figuresColors.base }}
          x={XPosP10}
          y={YPos + (BAND_HEIGHT - BAND_HEIGHT / 2)}
          onClick={onClick}
          tabIndex={onClick ? 0 : undefined}
          role={onClick ? "button" : undefined}
          aria-label="Chart Band"
        />
      )}

      {/* Bar */}
      <rect
        width={width}
        height={BAND_HEIGHT}
        rx={BAND_ROUNDNESS}
        ry={BAND_ROUNDNESS}
        className={cn("fill-info-lighten-100 stroke-info-base", {
          "cursor-pointer hover:fill-info-lighten-50 focus:ring-info-darken-100": !!onClick,
        })}
        style={
          isFiguresData
            ? {
                stroke: figuresColors.base,
                fill: figuresColors.light,
              }
            : undefined
        }
        x={XPos}
        y={YPos}
        onClick={onClick}
        tabIndex={onClick ? 0 : undefined}
        role={onClick ? "button" : undefined}
        aria-label="Chart Band"
      />
      {/* Benchmark  */}
      <rect
        height={BAND_HEIGHT}
        className="fill-info-base"
        width={1}
        x={BenchmarkXPos}
        y={YPos}
        style={isFiguresData ? { fill: figuresColors.base } : undefined}
      />

      {/* Benchmark value */}
      {isFiguresData && (
        <text
          key={data.benchmark}
          className="stroke-info-base"
          style={{
            fontSize: BAND_TEXT_SIZE,
            textAnchor: "middle",
            transform: `translateY(${BAND_TEXT_SIZE + BAND_TEXT_SPACING}px)`,
            ...(isFiguresData && { stroke: figuresColors.base }),
          }}
          x={BenchmarkXPos}
          y={YPos + BAND_HEIGHT}
        >
          {roundIfEnabled(
            new SimpleMoney(data.benchmark, currencyCode).format({
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
          )}
        </text>
      )}

      {/* Max value */}
      {!isFiguresData && (
        <text
          key={data.max}
          className="stroke-info-base"
          style={{
            fontSize: BAND_TEXT_SIZE,
            textAnchor: "middle",
            transform: `translateY(${BAND_TEXT_SIZE + BAND_TEXT_SPACING}px)`,
            stroke: isFiguresData ? figuresColors.base : undefined,
          }}
          x={XPos + width}
          y={YPos + BAND_HEIGHT}
        >
          {roundIfEnabled(
            new SimpleMoney(data.max, currencyCode).format({
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
          )}
        </text>
      )}

      {/* p90 vertical line */}
      {isFiguresData && data.p90 && (
        <rect
          height={12}
          width={1}
          x={XPosP90}
          y={YPos + 10}
          style={{ fill: figuresColors.base }}
        />
      )}

      {/* p75 - 90 horizontal line */}
      {isFiguresData && data.p90 && (
        <rect
          width={p90width}
          height={1}
          rx={BAND_ROUNDNESS}
          ry={BAND_ROUNDNESS}
          style={{ fill: figuresColors.base }}
          x={XPosP90 - p90width}
          y={YPos + (BAND_HEIGHT - BAND_HEIGHT / 2)}
          onClick={onClick}
          tabIndex={onClick ? 0 : undefined}
          role={onClick ? "button" : undefined}
          aria-label="Chart Band"
        />
      )}
    </>
  )
}
